import React, { useEffect, useState } from 'react';
import {
  Stack,
  Box,
  Card,
  Center,
  Input,
  Text,
  SimpleGrid,
  Alert,
  Link,
  Separator,
  Image,
} from '@chakra-ui/react';
import { format, startOfMonth, endOfMonth, subDays, subMonths } from 'date-fns';
import { NavLink, useParams } from 'react-router-dom';
import { SimpleSelect } from '@/components/ui/simple-select';
import ActionCard from '@/components/action-card';
import PieChart from '@/components/charts/pie';
import useWorkspace from '@/lib/hooks/use-workspace';
import useFetchActions from '@/lib/hooks/use-fetch-actions';
import useContentTypesStore from '@/utils/use-content-type';
import api from '@/utils/api';
import { calculateTotalCount } from '@/lib/helpers';
import { ActionType, ContentCount } from '@/lib/types';

const RANGE_ITEMS = [
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 4 weeks', value: '28' },
  { label: 'Last 3 months', value: '90' },
];

export default function Analytics() {
  const [contentCounts, setContentCounts] = useState<ContentCount[]>();
  const [chartLabels, setChartLabels] = useState<string[]>();
  const [chartData, setChartData] = useState<number[]>();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { showAlert } = useContentTypesStore();
  const { workspace } = useWorkspace();

  const [startDate, setStartDate] = useState<string>(
    format(startOfMonth(new Date()), 'yyyy-MM-dd'),
  );
  const [endDate, setEndDate] = useState<string>(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [rangePreset, setRangePreset] = useState('');

  const handleFilterChange = (value: string) => {
    setRangePreset(value);

    let startDate;
    let endDate = format(new Date(), 'yyyy-MM-dd');

    switch (value) {
      case 'today':
        startDate = endDate;
        break;
      case '7':
        startDate = format(subDays(new Date(), 6), 'yyyy-MM-dd');
        break;
      case '28':
        startDate = format(subDays(new Date(), 27), 'yyyy-MM-dd');
        break;
      case '90':
        startDate = format(subMonths(new Date(), 3), 'yyyy-MM-dd');
        break;
      default:
        startDate = format(startOfMonth(new Date()), 'yyyy-MM-dd');
        endDate = format(endOfMonth(new Date()), 'yyyy-MM-dd');
    }

    setStartDate(startDate);
    setEndDate(endDate);
  };

  const loginActions = useFetchActions(workspaceId!, ActionType.Login, startDate, endDate);

  const signupActions = useFetchActions(workspaceId!, ActionType.Signup, startDate, endDate);

  const loginCount = calculateTotalCount(loginActions);
  const signupCount = calculateTotalCount(signupActions);

  useEffect(() => {
    if (!workspaceId) return;

    async function fetchData() {
      const response = await api.get(`contents/counts/${workspaceId}`);
      setContentCounts(response.data.data || []);
      setChartLabels(response.data.data?.map((p: ContentCount) => p.content_type_name));
      setChartData(response.data.data?.map((p: ContentCount) => p.count));
    }
    fetchData();
  }, [workspaceId]);

  return (
    <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'} pb={6}>
      <Card.Header textAlign='center' mb={6}>
        {workspace && (
          <Image
            src={workspace?.logo || '/logo_horizontal.png'}
            alt={workspace?.name || 'Coach Hub'}
            height={'48px'}
            mx='auto'
            mb={3}
          />
        )}
        <Card.Title fontSize='3xl' mb={2}>
          {workspace?.name}
        </Card.Title>
        <Card.Description fontSize='sm' maxWidth='4xl' margin='auto'>
          {workspace?.description}
        </Card.Description>
      </Card.Header>
      <Separator />
      <Card.Body>
        <Stack gap={4}>
          <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
            <Box>
              <SimpleSelect
                items={RANGE_ITEMS}
                size='sm'
                w='40'
                value={rangePreset}
                onValueChange={handleFilterChange}
                placeholder='Select option'
              />
            </Box>
            <Box>
              <Input
                value={startDate}
                variant={'outline'}
                size={'sm'}
                type={'date'}
                max={'9999-12-31'}
                onChange={(e) => setStartDate(e.currentTarget.value)}
              />
            </Box>
            <Box>
              <Input
                value={endDate}
                variant={'outline'}
                size={'sm'}
                type={'date'}
                max={'9999-12-31'}
                onChange={(e) => setEndDate(e.currentTarget.value)}
              />
            </Box>
          </Stack>
          {showAlert && (
            <Alert.Root status={'warning'} borderRadius={'md'}>
              <Alert.Indicator />
              <Alert.Title>
                <strong>Action Required!</strong> You must create content library to view the data.
                Please visit the{' '}
                <Link fontWeight={'bold'} variant='underline' asChild>
                  <NavLink to={`/${workspaceId}/content-types`}>Settings Page</NavLink>
                </Link>{' '}
                to proceed.
              </Alert.Title>
            </Alert.Root>
          )}
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
            <ActionCard
              title='New Workspace Users'
              count={signupCount}
              actions={signupActions}
              workspaceId={workspaceId!}
              actionType={ActionType.Signup}
              startDate={startDate}
              endDate={endDate}
            />
            <ActionCard
              title='Active Workspace Users'
              count={loginCount}
              actions={loginActions}
              workspaceId={workspaceId!}
              actionType={ActionType.Login}
              startDate={startDate}
              endDate={endDate}
            />
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
            {chartData && chartData.length > 0 && (
              <Card.Root borderWidth={'1px'} borderRadius={'lg'} borderColor={'border'}>
                <Card.Header pb={1} pt={3} px={5}>
                  <Text
                    fontSize={'sm'}
                    fontWeight={'medium'}
                    textTransform={'capitalize'}
                    color={'fg.muted'}
                  >
                    Number of Contents
                  </Text>
                </Card.Header>
                <Card.Body py={3}>
                  {chartLabels && chartLabels.length > 0 && (
                    <Center>
                      <PieChart labels={chartLabels} data={chartData} />
                    </Center>
                  )}
                </Card.Body>
              </Card.Root>
            )}
            <Box>
              <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
                {contentCounts &&
                  contentCounts.map((contentCount) => (
                    <Card.Root
                      key={contentCount.content_type_id}
                      borderWidth={'1px'}
                      borderRadius={'lg'}
                      borderColor={'border'}
                    >
                      <Card.Header pb={0} pt={3} px={5}>
                        <Text
                          fontSize={'sm'}
                          fontWeight={'medium'}
                          textTransform={'capitalize'}
                          color={'fg.muted'}
                        >
                          {contentCount.content_type_name}
                        </Text>
                      </Card.Header>
                      <Card.Body pt={0} pb={3}>
                        <Center>
                          <Text fontSize={'5xl'} as={'b'}>
                            {contentCount.count}
                          </Text>
                        </Center>
                      </Card.Body>
                    </Card.Root>
                  ))}
              </SimpleGrid>
            </Box>
          </SimpleGrid>
        </Stack>
      </Card.Body>
    </Card.Root>
  );
}
